import React, { useMemo } from 'react';
import useSortableTable from './useSortableTable';
import './SortableTable.css';
import { formatCurrency, formatDecimalPlaces, getRowKey, normalCDF, estimateDelta } from './helpers';

function OptionTable({ options, onDelete, onEdit, onUpdate, editQuantity, setEditQuantity, editPremium, setEditPremium, editingPosition }) {
  // Added 'delta' column name to the columns definition array
  const columns = ['position', 'quantity', 'option_type', 'strike_price', 'expiration_date', 'Price', 'delta', 'option_premium', 'premium_paid', 'exposure'];
  const { sortedData, requestSort, getSortClass } = useSortableTable(options, columns);

  const { enrichedData, totals } = useMemo(() => {
    const acc = {
      putExposure: 0,
      callExposure: 0,
      deltaWeightedPutExposure: 0,
      deltaWeightedCallExposure: 0,
      putAssignmentRisk: 0,
      callAssignmentRisk: 0,
      totalMarketValue: 0
    };

    const data = sortedData.map(item => {
      const underlyingPrice = item.details?.Price || 0;
      const isITM = item.option_type === 'put'
        ? underlyingPrice < item.strike_price
        : underlyingPrice > item.strike_price;

      const multiplier = item.option_type === 'put' ? -100 : 100;
      const exposure = item.quantity * item.strike_price * multiplier;

      const marketValue = (item.option_premium || 0) * 100 * item.quantity;

      // Calculate estimated delta percentage (e.g. 45 or -32)
      const delta = estimateDelta(underlyingPrice, item.strike_price, item.expiration_date, item.option_type);

      // Convert delta to a probability multiplier (0 to 1)
      const deltaNum = parseFloat(delta) || 0;
      const probFactor = Math.abs(deltaNum) > 1 ? Math.abs(deltaNum) / 100 : Math.abs(deltaNum);
      const deltaWeightedExposure = exposure * probFactor;

      if (item.option_type === 'put') {
        acc.putExposure += exposure;
        acc.deltaWeightedPutExposure += deltaWeightedExposure;
        if (isITM) acc.putAssignmentRisk += exposure;
      } else {
        acc.callExposure += exposure;
        acc.deltaWeightedCallExposure += deltaWeightedExposure;
        if (isITM) acc.callAssignmentRisk += exposure;
      }
      acc.totalMarketValue += marketValue;

      return {
        ...item,
        exposure,
        isITM,
        underlyingPrice,
        marketValue,
        delta
      };
    });

    return { enrichedData: data, totals: acc };
  }, [sortedData]);

  return (
    <div>
      <h2>Options</h2>
      <div className="table-responsive-wrapper">
        <table>
          <thead>
            <tr>
              <th onClick={() => requestSort('position')} className={getSortClass('position')}>Ticker</th>
              <th>Quantity</th>
              <th>Type</th>
              <th onClick={() => requestSort('strike_price')} className={getSortClass('strike_price')}>Strike</th>
              <th onClick={() => requestSort('expiration_date')} className={getSortClass('expiration_date')}>Expiry</th>
              <th>Underlying Price</th>
              {/* Added Delta Header */}
              <th onClick={() => requestSort('delta')} className={getSortClass('delta')}>Delta</th>
              <th onClick={() => requestSort('option_premium')} className={getSortClass('option_premium')}>Premium</th>
              <th onClick={() => requestSort('premium_paid')} className={getSortClass('premium_paid')}>Premium Paid</th>
              <th onClick={() => requestSort('exposure')} className={getSortClass('exposure')}>Exposure</th>
              <th onClick={() => requestSort('DFVScore')} className={getSortClass('DFVScore')}>DFVScore</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {enrichedData.map((item, index) => {
              const expiryDate = new Date(item.expiration_date + 'T00:00:00');
              const diffDays = Math.ceil((expiryDate - new Date()) / (1000 * 60 * 60 * 24));
              const isNearExpiry = diffDays >= 0 && diffDays <= 7;

              return (
                <tr key={index}>
                  <td><strong>{item.position}</strong></td>
                  <td>{item.quantity}</td>
                  <td style={{ fontWeight: 'bold' }}>{item.option_type?.toUpperCase()}</td>
                  <td>{formatCurrency(item.strike_price)}</td>
                  <td style={{ color: isNearExpiry ? '#f39c12' : 'inherit', fontWeight: isNearExpiry ? 'bold' : 'normal' }}>
                    {item.expiration_date ? (() => {
                      const [year, month, day] = item.expiration_date.split('-');
                      return `${month}/${day}/${year}`;
                    })() : '-'}
                  </td>
                  <td style={{ color: item.isITM ? '#e74c3c' : 'inherit', fontWeight: item.isITM ? 'bold' : 'normal' }}>
                    {formatCurrency(item.underlyingPrice)}
                  </td>
                  {/* Added Delta Body Column formatted nicely */}
                  <td style={{ fontWeight: '500' }}>
                    {item.delta ? `${item.delta}` : '0'}
                  </td>
                  <td>{formatCurrency(item.option_premium || 0)}</td>
                  <td>{formatCurrency(item.marketValue)}</td>
                  <td>{formatCurrency(item.exposure)}</td>
                  <td>{formatDecimalPlaces(item.details?.DFVScore, 2)}</td>
                  <td>
                    <button onClick={() => onDelete(item)}>Delete</button>
                    {editingPosition === getRowKey(item) ? (
                      <div style={{ marginTop: '5px' }}>
                        <input type="number" placeholder="Qty" style={{ width: '50px' }} value={editQuantity} onChange={(e) => setEditQuantity(e.target.value)} />
                        <input type="number" step="0.01" placeholder="Prem" style={{ width: '60px', marginLeft: '5px' }} value={editPremium} onChange={(e) => setEditPremium(e.target.value)} />

                        <button style={{ marginLeft: '5px' }} onClick={() => onUpdate(item)}>Save</button>
                        <button onClick={() => { onEdit(null); setEditPremium(''); }}>Cancel</button>
                      </div>
                    ) : (
                      <button onClick={() => {
                        onEdit(getRowKey(item));
                        setEditQuantity(item.quantity);
                        setEditPremium(item.option_premium || 0);
                      }}>Edit</button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot style={{ backgroundColor: '#f9f9f9', fontWeight: 'bold' }}>
            {/* Incremented colSpans by 1 to accommodate the new Delta column layout */}
            <tr>
              <td colSpan="8" style={{ textAlign: 'right' }}>Total Premium Paid:</td>
              <td>{formatCurrency(totals.totalMarketValue)}</td>
              <td colSpan="3"></td>
            </tr>
            <tr>
              <td colSpan="9" style={{ textAlign: 'right' }}>Total Put Exposure:</td>
              <td>{formatCurrency(totals.putExposure)}</td>
              <td colSpan="2" style={{ color: '#e74c3c', fontSize: '0.85em' }}>
                ITM Risk: {formatCurrency(totals.putAssignmentRisk)}
              </td>
            </tr>
            <tr>
              <td colSpan="9" style={{ textAlign: 'right' }}>Total Call Exposure:</td>
              <td>{formatCurrency(totals.callExposure)}</td>
              <td colSpan="2" style={{ color: '#e74c3c', fontSize: '0.85em' }}>
                ITM Risk: {formatCurrency(totals.callAssignmentRisk)}
              </td>
            </tr>
            {/* Added Delta-Weighted Summary Rows */}
            <tr>
              <td colSpan="9" style={{ textAlign: 'right' }}>Delta-Weighted Put Exposure:</td>
              <td>{formatCurrency(totals.deltaWeightedPutExposure)}</td>
              <td colSpan="2"></td>
            </tr>
            <tr>
              <td colSpan="9" style={{ textAlign: 'right' }}>Delta-Weighted Call Exposure:</td>
              <td>{formatCurrency(totals.deltaWeightedCallExposure)}</td>
              <td colSpan="2"></td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}

export default OptionTable;
